import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { WizardConfig, WizardErrors, WIZARD_STEPS } from '../types/wizard'
import { validateCalendlyUrl } from '../utils/validateCalendly'
import Step1AgentInfo from './wizard/Step1AgentInfo'
import Step2Calendly from './wizard/Step2Calendly'
import Step3Market from './wizard/Step3Market'
import Step4Branding from './wizard/Step4Branding'
import Step5Review from './wizard/Step5Review'

export default function ConfigWizard() {
  const navigate = useNavigate()
  const [currentStep, setCurrentStep] = useState(1)
  const [errors, setErrors] = useState<WizardErrors>({})

  const [config, setConfig] = useState<WizardConfig>({
    agentName: '',
    email: '',
    phone: '',
    calendlyUrl: '',
    cities: [],
    priceRanges: [],
    primaryColor: '#2563eb',
    botNamePreference: '',
    clientId: '',
  })

  const updateConfig = (updates: Partial<WizardConfig>) => {
    setConfig(prev => ({ ...prev, ...updates }))
  }

  const validateStep = (step: number): boolean => {
    const newErrors: WizardErrors = {}

    switch (step) {
      case 1:
        if (!config.agentName.trim()) newErrors.agentName = 'Name is required'
        if (!config.email.trim()) {
          newErrors.email = 'Email is required'
        } else if (!/\S+@\S+\.\S+/.test(config.email)) {
          newErrors.email = 'Invalid email format'
        }
        if (!config.phone.trim()) newErrors.phone = 'Phone is required'
        break

      case 2:
        const calendlyValidation = validateCalendlyUrl(config.calendlyUrl)
        if (!calendlyValidation.valid) {
          newErrors.calendlyUrl = calendlyValidation.error
        }
        break

      case 3:
        if (config.cities.length === 0) newErrors.cities = 'Select at least one city'
        if (config.priceRanges.length === 0) newErrors.priceRanges = 'Select at least one price range'
        break

      case 4:
        // Branding is all optional
        break
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleNext = () => {
    if (validateStep(currentStep)) {
      if (currentStep < 5) {
        setCurrentStep(currentStep + 1)
      }
    }
  }

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1)
      setErrors({})
    }
  }

  const handlePreview = () => {
    // Generate clientId from agent name
    const clientId = config.agentName
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '')

    updateConfig({ clientId })

    // Store config in session storage for preview
    sessionStorage.setItem('wizardConfig', JSON.stringify({ ...config, clientId }))

    navigate('/preview')
  }

  const handleCheckout = () => {
    // Generate clientId from agent name
    const clientId = config.agentName
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '')

    updateConfig({ clientId })

    // Store config for checkout
    sessionStorage.setItem('wizardConfig', JSON.stringify({ ...config, clientId }))
    navigate('/checkout')
  }

  return (
    <div className="max-w-4xl mx-auto">
      {/* Progress Bar */}
      <div className="mb-8">
        <div className="flex justify-between mb-4">
          {WIZARD_STEPS.map((step) => (
            <div key={step.number} className="flex flex-col items-center flex-1">
              <div
                className={`w-10 h-10 rounded-full flex items-center justify-center font-bold mb-2 ${
                  currentStep >= step.number
                    ? 'bg-primary-600 text-white'
                    : 'bg-gray-200 text-gray-500'
                }`}
              >
                {step.number}
              </div>
              <div className="text-center">
                <div className={`text-sm font-semibold ${
                  currentStep >= step.number ? 'text-gray-900' : 'text-gray-400'
                }`}>
                  {step.title}
                </div>
                <div className="text-xs text-gray-500 hidden sm:block">{step.description}</div>
              </div>
            </div>
          ))}
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div
            className="bg-primary-600 h-2 rounded-full transition-all duration-300"
            style={{ width: `${(currentStep / 5) * 100}%` }}
          />
        </div>
      </div>

      {/* Step Content */}
      <div className="bg-white rounded-2xl shadow-lg p-8 mb-6">
        {currentStep === 1 && (
          <Step1AgentInfo config={config} updateConfig={updateConfig} errors={errors} />
        )}
        {currentStep === 2 && (
          <Step2Calendly config={config} updateConfig={updateConfig} errors={errors} />
        )}
        {currentStep === 3 && (
          <Step3Market config={config} updateConfig={updateConfig} errors={errors} />
        )}
        {currentStep === 4 && (
          <Step4Branding config={config} updateConfig={updateConfig} errors={errors} />
        )}
        {currentStep === 5 && (
          <Step5Review config={config} />
        )}
      </div>

      {/* Navigation Buttons */}
      <div className="flex justify-between items-center">
        <button
          onClick={handleBack}
          disabled={currentStep === 1}
          className={`px-6 py-3 rounded-lg font-semibold ${
            currentStep === 1
              ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          }`}
        >
          Back
        </button>

        <div className="flex gap-3">
          {currentStep === 4 && (
            <button
              onClick={handlePreview}
              className="px-6 py-3 rounded-lg font-semibold bg-white text-primary-600 border-2 border-primary-600 hover:bg-primary-50"
            >
              Preview Bot
            </button>
          )}

          {currentStep < 5 ? (
            <button
              onClick={handleNext}
              className="btn-primary"
            >
              Next
            </button>
          ) : (
            <button
              onClick={handleCheckout}
              className="btn-primary"
            >
              Proceed to Checkout
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
